<script setup lang="ts">
import ListingDetailBasic from '@/modules/realestate/views/ListingDetailBasic.vue'
import ListingDetailContent from '@/modules/realestate/views/ListingDetailContent.vue'
import ListingDetailMedia from '@/modules/realestate/views/ListingDetailMedia.vue'
import ListingDetailAsset from '@/modules/realestate/views/ListingDetailAsset.vue'
import ListingListByAsset from '@/modules/realestate/components/ListingListByAsset.vue'
import ConsultantRequestByListing from '@/modules/contract/components/ConsultantRequestByListing.vue'
import FlashOfferListByListing from '@/modules/flashoffer/components/FlashOfferListByListing.vue'
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import {
  useListingDetailStore,
  ListingDetailActionType,
} from '@/modules/realestate/stores/listing.detail.store.ts'
import '@/modules/realestate/assets/scss/listing.scss'
import { Icon } from '@iconify/vue'
import ListingDetailContract from '@/modules/realestate/views/ListingDetailContract.vue'
import ContractUpdateForm from '@/modules/contract/components/ContractUpdateForm.vue'
import AccountCard from '@/onenetwork/components/account_card/AccountCard.vue'
import ContractDetailForm from '@/modules/contract/components/ContractDetailForm.vue'
import { useToast } from 'primevue/usetoast'
import {
  initialValues as _initialValues,
  mappingValuesToForm,
} from '@/modules/realestate/helper/listing'
import {
  LISTING_TYPE,
  REQUEST_TYPE,
  REQUEST_TYPE_OPTIONS,
} from '@/modules/realestate/constants/realestate.const'

import { useConfirm } from 'primevue'
import { schemaListing } from '../helper/schema'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import AllowPerm from '@/onenetwork/components/perms/AllowPerm.vue'
import CsButton from '@/components/ui/button/CsButton.vue'

const toast = useToast()
const confirm = useConfirm()

interface Props {
  id: number
  approveForm?: boolean
  reloadList?: any
}

const listingDetailStore = useListingDetailStore()

const props = defineProps<Props>()

const editable = ref<boolean>(false)
const approveAble = ref<boolean>(true)
const contractCreateFormDisplay = ref<boolean>(false)
const contractDetailFormDisplay = ref<boolean>(false)
const contractId = ref<number | null>(null)

const listingDetail = computed<any>(() => {
  try {
    return listingDetailStore.listings[props.id]?.data
  } catch (e) {
    return null
  }
})
const fetched = computed(() => {
  return listingDetailStore.listings[props.id]?.action_type
})

const formRef = ref()
const initialValues = ref<any>(_initialValues)

const requestFetchDetail = async () => {
  try {
    await listingDetailStore.fetchListingDetail(props.id, props.approveForm)
  } catch (e) {
    console.error(e)
  }
}

watch(
  listingDetail,
  () => {
    if (listingDetail.value) {
      contractId.value = listingDetail.value?.contract?.id
      initialValues.value = mappingValuesToForm(listingDetail.value)

      nextTick(() => {
        formRef.value?.reset()
      })
    }
  },
  { immediate: true, deep: true },
)

const resolver = yupResolver(schemaListing)

const onFormSubmit = async ({ values, valid }: { values: Record<string, any>; valid: boolean }) => {
  if (!valid) {
    return
  }

  // Lấy dữ liệu công năng từ thẻ HTML ẩn
  const hiddenAttrs = document.getElementById('listing_hidden_attributes') as HTMLInputElement
  if (hiddenAttrs && hiddenAttrs.value) {
    try {
      values.attributes = JSON.parse(hiddenAttrs.value)
    } catch (e) {
      console.error(e)
    }
  } else if (!values.attributes) {
    values.attributes = initialValues.value.attributes || []
  }

  // get form values
  confirm.require({
    message: 'Bạn có muốn lưu các chỉnh sửa?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: async () => {
      await listingDetailStore.updateListingDetail(props.id, values)
      // initialValues.value = values
      editable.value = false
      formRef.value?.reset()
      toast.add({ severity: 'info', summary: 'Confirmed', detail: 'You have accepted', life: 3000 })
      props.reloadList?.()
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const handleEditing = (e: Event) => {
  editable.value = true
}

const loadingUpdate = computed(() => {
  return listingDetailStore.listings[props.id]?.action_type === ListingDetailActionType.UPDATING
})

const handleSave = (e: Event) => {
  const formEl = formRef.value.$el as HTMLFormElement

  const event = new Event('submit', {
    bubbles: true, // Whether the event will bubble up through the DOM or not
    cancelable: true, // Whether the event may be canceled or not
  })

  formEl.dispatchEvent(event)
}
const handleReset = (e: Event) => {
  editable.value = false
  // reset form
  formRef.value?.reset()
  contractId.value = initialValues.value.contract_id
}

const loadingApprove = computed(() => {
  return listingDetailStore.listings[props.id]?.action_type === ListingDetailActionType.APPROVING
})
const handleApprove = async () => {
  try {
    await listingDetailStore.approveListing(
      props.id,
      listingDetail.value?.request_type === REQUEST_TYPE.UPDATE
        ? listingDetail.value?.new_data
        : null,
    )
    approveAble.value = false
    toast.add({ severity: 'info', summary: 'Confirmed', detail: 'You have accepted', life: 3000 })
    props.reloadList?.()
  } catch (e) {
    console.error(e)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while approving the listing',
      life: 3000,
    })
  }
}

const loadingReject = computed(() => {
  return listingDetailStore.listings[props.id]?.action_type === ListingDetailActionType.REJECTING
})
const handleReject = async () => {
  try {
    await listingDetailStore.rejectListing(props.id)
    approveAble.value = false
    toast.add({ severity: 'info', summary: 'Rejected', detail: 'You have rejected', life: 3000 })
    props.reloadList?.()
  } catch (e) {
    console.error(e)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while rejecting the listing',
      life: 3000,
    })
  }
}

const handleCreatedContract = (data: any) => {
  if (data.id) {
    contractCreateFormDisplay.value = false
    contractId.value = data.id
    formRef.value?.setFieldValue('customer_id', data.seller?.id)
    formRef.value?.setFieldValue('broker_id', data.seller_broker?.id)
    formRef.value?.setFieldValue('contract_id', data.id)
  }
}

const handleChangeDetailContract = (data: any) => {
  if (data?.id) {
    contractId.value = data.id
    formRef.value?.setFieldValue('customer_id', data.seller?.id)
    formRef.value?.setFieldValue('broker_id', data.seller_broker?.id)
    formRef.value?.setFieldValue('contract_id', data.id)
  } else {
    contractId.value = null
    formRef.value?.setFieldValue('customer_id', null)
    formRef.value?.setFieldValue('broker_id', null)
    formRef.value?.setFieldValue('contract_id', null)
  }
}

// Checklist for approval
const approvalChecklist = ref({
  basicInfo: false,
  images: false,
  price: false,
  legal: false,
  description: false,
  contact: false,
})

const checklistItems = computed(() => [
  {
    key: 'basicInfo',
    label: 'Thông tin cơ bản đầy đủ',
    description: 'Tiêu đề, loại BĐS, địa chỉ, diện tích',
    checked: approvalChecklist.value.basicInfo,
  },
  {
    key: 'images',
    label: 'Hình ảnh đầy đủ',
    description: 'Tối thiểu 3 ảnh, chất lượng tốt',
    checked: approvalChecklist.value.images,
  },
  {
    key: 'price',
    label: 'Giá cả hợp lý',
    description: 'Giá phù hợp với thị trường, không quá cao hoặc thấp',
    checked: approvalChecklist.value.price,
  },
  {
    key: 'legal',
    label: 'Pháp lý rõ ràng',
    description: 'Thông tin pháp lý, sổ đỏ, giấy tờ đầy đủ',
    checked: approvalChecklist.value.legal,
  },
  {
    key: 'description',
    label: 'Mô tả chi tiết',
    description: 'Nội dung mô tả đầy đủ, không vi phạm quy định',
    checked: approvalChecklist.value.description,
  },
  {
    key: 'contact',
    label: 'Thông tin liên hệ chính xác',
    description: 'Thông tin người đăng, số điện thoại hợp lệ',
    checked: approvalChecklist.value.contact,
  },
])

const allChecklistCompleted = computed(() => {
  return Object.values(approvalChecklist.value).every((checked) => checked === true)
})

const toggleChecklistItem = (key: string) => {
  if (approvalChecklist.value.hasOwnProperty(key)) {
    approvalChecklist.value[key] = !approvalChecklist.value[key]
  }
}

onMounted(() => {
  requestFetchDetail()
})
</script>

<template>
  <!-- {{ listingDetail }} -->
  <template v-if="ListingDetailActionType.FETCHING === fetched">
    <Card>
      <template #content>
        <div class="flex gap-3">
          Đang tải dữ liệu <Icon icon="eos-icons:three-dots-loading" width="24" height="24" />
        </div>
      </template>
    </Card>
  </template>
  <template v-if="ListingDetailActionType.FETCH_FAILED === fetched">
    <Card class="bg-red-300">
      <template #content>
        <div class="flex flex-col gap-3 items-center">
          <Icon icon="fluent-color:error-circle-16" width="50" />
          <span class="font-300 text-[16px]">Lỗi khi tải dữ liệu này</span>
        </div>
      </template>
    </Card>
  </template>
  <template
    v-if="
      ![ListingDetailActionType.FETCHING, ListingDetailActionType.FETCH_FAILED].includes(fetched)
    "
  >
    <div class="">
      <div class="flex justify-end gap-4 mb-5">
        <AllowPerm perm="real_estate.edit" action="hide">
          <div class="flex gap-2">
            <!-- <button
              class="btn btn-secondary flex items-center gap-3"
              v-if="
                !editable &&
                ![REQUEST_TYPE.DELETE, REQUEST_TYPE.UPDATE].includes(listingDetail?.request_type)
              "
              @click="handleEditing"
            >
              <Icon icon="mynaui:edit-solid" width="16px" height="16px" /> Chỉnh sửa
            </button> -->
            <CsButton
              type="button"
              text="Chỉnh sửa"
              @click="handleEditing"
              :visible="
                !editable &&
                ![REQUEST_TYPE.DELETE, REQUEST_TYPE.UPDATE].includes(listingDetail?.request_type)
              "
            />
            <!-- <button
              class="btn btn-success flex items-center gap-3"
              v-if="editable"
              @click="handleSave"
            >
              <Icon icon="ri:save-3-fill" width="16px" height="16px" /> Lưu chỉnh sửa
              <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loadingUpdate" />
            </button> -->
            <CsButton
              type="button"
              text="Lưu chỉnh sửa"
              @click="handleSave"
              :visible="editable"
              :loading="loadingUpdate"
            />
            <!-- <button
              class="btn btn-outline flex items-center gap-3"
              v-if="editable"
              @click="handleReset"
            >
              <Icon icon="ri:reset-left-fill" width="16px" height="16px" /> Reset chỉnh sửa
            </button> -->
            <CsButton
              type="button"
              variant="danger"
              icon="ri:close-circle-fill"
              text="Hủy chỉnh sửa"
              @click="handleReset"
              :visible="editable"
            />
          </div>
        </AllowPerm>
        <AllowPerm perm="real_estate.approve" action="hide">
          <div
            class="flex gap-2 border-l border-gray-200 pl-4"
            v-if="approveAble && approveForm && !editable"
          >
            <!-- <button
              class="btn btn-success flex items-center gap-3"
              @click="handleApprove"
              :disabled="loadingApprove"
            >
              <Icon icon="mdi:check-all" width="16px" height="16px" /> Phê duyệt
              {{
                REQUEST_TYPE_OPTIONS.find((item) => item.value === listingDetail?.request_type)?.label
              }}
              <Icon
                icon="eos-icons:three-dots-loading"
                width="24"
                height="24"
                v-if="loadingApprove"
              />
            </button> -->
            <CsButton
              type="button"
              :text="`Phê duyệt ${REQUEST_TYPE_OPTIONS.find((item) => item.value === listingDetail?.request_type)?.label}`"
              @click="handleApprove"
              :loading="loadingApprove"
              :disabled="loadingApprove || !allChecklistCompleted"
              icon="mdi:check-all"
            />
            <!-- <button class="btn btn-danger flex items-center gap-3" @click="handleReject">
              <Icon icon="healthicons:i-documents-denied" width="16px" height="16px" /> Từ chối phê
              duyệt
              {{
                REQUEST_TYPE_OPTIONS.find((item) => item.value === listingDetail?.request_type)?.label
              }}
              <Icon icon="eos-icons:three-dots-loading" width="24" height="24" v-if="loadingReject" />
            </button> -->
            <CsButton
              type="button"
              variant="danger"
              :text="`Từ chối phê duyệt ${REQUEST_TYPE_OPTIONS.find((item) => item.value === listingDetail?.request_type)?.label}`"
              @click="handleReject"
              :loading="loadingReject"
              icon="healthicons:i-documents-denied"
            />
          </div>
        </AllowPerm>
      </div>
      <Form
        ref="formRef"
        v-slot="$form"
        :initialValues
        :resolver
        @submit="onFormSubmit"
        class="flex flex-col md:flex-row w-full gap-3 mb-5"
      >
        <div class="flex-1 flex flex-col gap-3" v-if="approveForm">
          <Panel header="Checklist phê duyệt" toggleable>
            <div class="flex flex-col gap-3">
              <div class="text-sm text-gray-600 mb-2">
                Vui lòng kiểm tra các mục sau trước khi phê duyệt tin đăng:
              </div>

              <div
                v-for="item in checklistItems"
                :key="item.key"
                class="flex flex-col gap-1 p-3 rounded hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-start gap-3">
                  <Checkbox
                    :inputId="`checklist-${item.key}`"
                    :modelValue="item.checked"
                    @update:modelValue="() => toggleChecklistItem(item.key)"
                    binary
                  />
                  <div class="flex-1">
                    <label :for="`checklist-${item.key}`" class="font-medium cursor-pointer">
                      {{ item.label }}
                    </label>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ item.description }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="!allChecklistCompleted"
                class="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800"
              >
                ⚠️ Vui lòng hoàn thành tất cả các mục kiểm tra trước khi phê duyệt
              </div>

              <div
                v-else
                class="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800"
              >
                ✓ Đã hoàn thành tất cả các mục kiểm tra. Có thể phê duyệt tin đăng.
              </div>
            </div>
          </Panel>
        </div>
        <div class="flex-3 flex flex-col gap-3">
          <Panel header="Thông tin cơ bản" toggleable>
            <ListingDetailBasic
              :editable="editable"
              :form="formRef"
              :listing="listingDetail"
              :newData="
                listingDetail?.request_type === REQUEST_TYPE.UPDATE ? listingDetail.new_data : null
              "
              :approveForm="props.approveForm"
            />
          </Panel>
          <Panel header="Nội dung" toggleable>
            <ListingDetailContent
              :editable="editable"
              :form="formRef"
              :newData="
                listingDetail?.request_type === REQUEST_TYPE.UPDATE ? listingDetail.new_data : null
              "
            />
          </Panel>
          <Panel header="Media" toggleable>
            <ListingDetailMedia
              :editable="editable"
              :form="formRef"
              :newData="
                listingDetail?.request_type === REQUEST_TYPE.UPDATE ? listingDetail.new_data : null
              "
            />
          </Panel>
        </div>
        <div class="flex-2 flex flex-col gap-3">
          <Panel header="Thông tin BĐS" toggleable>
            <ListingDetailAsset
              :listing="listingDetail"
              :editable="editable"
              :form="formRef"
              :newData="
                listingDetail?.request_type === REQUEST_TYPE.UPDATE ? listingDetail.new_data : null
              "
            />
          </Panel>
          <Panel
            toggleable
            v-if="
              [LISTING_TYPE.EXCLUSIVE, LISTING_TYPE.OWNER].includes(
                $form?.listing_type?.value?.code,
              )
            "
          >
            <template #header>
              <div class="flex w-full items-center justify-between">
                <div class="p-panel-title">Thông tin hợp đồng</div>
                <AllowPerm perm="contract.create" action="hide">
                  <div class="text-right" v-if="editable">
                    <a
                      href="#"
                      class="flex items-center hover:text-[#0000FF]"
                      @click="
                        () => {
                          contractCreateFormDisplay = true
                        }
                      "
                    >
                      <Icon icon="ic:baseline-plus" /> Thêm hợp đồng
                    </a>
                  </div>
                </AllowPerm>
              </div>
            </template>
            <AllowPerm perm="real_estate.view_contract" action="warning">
              <ListingDetailContract
                :contract-id="contractId"
                :listingId="props.id"
                :editable="editable"
                @change="handleChangeDetailContract"
                @show:detail="
                  () => {
                    contractDetailFormDisplay = true
                  }
                "
              />
              <span class="font-italic text-[12px] text-red-500">
                {{ $form?.contract_id?.error?.message }}
              </span>
            </AllowPerm>
          </Panel>

          <Panel toggleable header="Thông tin người đăng" v-if="listingDetail?.poster">
            <account-card :data="listingDetail?.poster" account-type="customer" />
          </Panel>
        </div>
      </Form>
      <Tabs value="1" scrollable v-if="!approveForm">
        <TabList>
          <Tab value="1">Tin đăng cùng BDS</Tab>
          <AllowPerm perm="real_estate.view_consumer" action="hide">
            <Tab value="2">Khách hàng cần tư vấn</Tab>
          </AllowPerm>
          <AllowPerm perm="real_estate.view_flash_offer" action="hide">
            <Tab value="3">Quản lý Flash Offer</Tab>
          </AllowPerm>
        </TabList>
        <TabPanels>
          <TabPanel value="1">
            <ListingListByAsset
              v-if="listingDetail?.real_estate_id"
              :id="listingDetail?.real_estate_id"
              :exclude_id="listingDetail?.id"
            />
          </TabPanel>
          <AllowPerm perm="real_estate.view_consumer" action="hide">
            <TabPanel value="2">
              <ConsultantRequestByListing
                v-if="listingDetail?.real_estate_id"
                :id="listingDetail?.id"
              />
            </TabPanel>
          </AllowPerm>
          <AllowPerm perm="real_estate.view_flash_offer" action="hide">
            <TabPanel value="3">
              <FlashOfferListByListing
                v-if="listingDetail?.real_estate_id"
                :id="listingDetail?.id"
              />
            </TabPanel>
          </AllowPerm>
        </TabPanels>
      </Tabs>
    </div>
    <Drawer v-model:visible="contractCreateFormDisplay" header="Tạo hợp đồng mới" position="right">
      <div class="drawer-80vw">
        <ContractUpdateForm
          :listing-id="props.id"
          :contract-id="null"
          action="create"
          @on:created="handleCreatedContract"
        />
      </div>
    </Drawer>
    <Drawer v-model:visible="contractDetailFormDisplay" header="Chi tiết hợp đồng" position="right">
      <div class="drawer-80vw">
        <contract-detail-form :contract-id="contractId" />
      </div>
    </Drawer>
  </template>
</template>

<style scoped></style>
